import { cn } from '../lib';
import { Input } from '../shadcn';
import { Typography } from './Typography';
import { Loader2Icon } from 'lucide-react';
import { FC, RefObject, useRef } from 'react';
import { AddressSuggestion } from '@/entities';
import { useCloseOnOutsideClick, useRTL } from '../hooks';
import { selectIsMinimalistic, useAppSelector } from '@/app/store';

interface SuggesterProps {
  value: string;
  loading: boolean;
  className?: string;
  inCatalog?: boolean;
  isDesktop?: boolean;
  placeholder?: string;
  inputClassName?: string;
  onSuccess: VoidFunction;
  suggestions: AddressSuggestion[];
  setValue: (value: string) => void;
  onSuggestionSelect: (suggestion: AddressSuggestion) => void;
}

export const Suggester: FC<SuggesterProps> = ({
  value,
  loading,
  setValue,
  className,
  onSuccess,
  isDesktop,
  suggestions,
  placeholder,
  inputClassName,
  inCatalog = false,
  onSuggestionSelect,
}) => {
  const isRTL = useRTL();
  const isMinimalistic = useAppSelector(selectIsMinimalistic);

  const inputRef = useRef<HTMLInputElement>(null);
  const selectorRef = useRef({}) as RefObject<HTMLDivElement>;
  useCloseOnOutsideClick([selectorRef], onSuccess);

  const handleInputRef = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (node) {
      node.focus();
    }
  };

  return (
    <div ref={selectorRef} className={cn(inCatalog ? 'w-full' : 'relative', className)}>
      <Input
        ref={handleInputRef}
        autoFocus
        value={value}
        disabled={loading}
        placeholder={placeholder ?? ''}
        onChange={(e) => setValue(e.target.value)}
        className={cn(
          isRTL ? 'pl-8 text-right' : 'pr-8',
          inputClassName,
          inCatalog && 'border-none shadow-none rounded-full bg-address-background',
          inCatalog && (isRTL ? 'pl-10' : 'pr-10'),
        )}
      />

      {loading && (
        <div
          className={cn(
            'absolute flex items-center',
            isRTL ? 'left-0 pl-3' : 'right-0 pr-3',
            inCatalog ? '-mt-[28px]' : 'inset-y-0',
          )}
        >
          <Loader2Icon size={16} className='animate-spin' />
        </div>
      )}

      {suggestions.length > 0 && (
        <ul
          className={cn(
            'p-1 bg-background border border-solid rounded-md shadow-sm overflow-auto mt-1 z-10',
            isMinimalistic && 'rounded-none shadow-none',
            inCatalog
              ? cn(
                  'fixed max-h-30 rounded-xl bg-address-background',
                  isDesktop ? 'w-[300px] max-h-50' : 'w-[calc(100vw-76px)]',
                )
              : 'w-full absolute max-h-40',
            isRTL && 'text-right',
          )}
        >
          {suggestions.map((suggestion, index) => {
            return (
              <li
                key={index}
                onClick={() => onSuggestionSelect(suggestion)}
                className={cn(
                  'p-2 hover:bg-secondary rounded-xs cursor-pointer',
                  isRTL && 'text-right',
                  inCatalog && 'rounded-lg',
                )}
              >
                <Typography variant='text2'>{suggestion.value}</Typography>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
